import React, { useState } from 'react';

import { EnvironmentOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { SearchForecastDTO } from '@recurrency/core-api-schema/dist/search/getSearchForecasts';
import { Empty } from 'antd';
import moment, { Moment } from 'moment';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { ForecastChart, ForecastDataType } from 'components/Charts/ForecastChart';
import { FilterBarBox } from 'components/FilterBarBox';
import { ExternalLink } from 'components/Links';
import { SmallLoader } from 'components/Loaders';
import { Tabs } from 'components/Tabs';

import { useCoreApi } from 'hooks/useApi';

import { truthy } from 'utils/boolean';
import { DateFilter, getDefaultDateFilter } from 'utils/date';
import { splitIdNameStr, splitIfIdNameStr } from 'utils/formatting';

import { IdNameObj } from 'types/legacy-api';

import { DemandUsageLinesTable, UsageLineOverride } from './DemandUsageLinesTable';
import {
  ForecastOverrideTable,
  getMappedUsageAndForecastRecords,
  MappedUsageForecastRecord,
  RecordType,
} from './ForecastOverrideTable';
import { LastUpdatedMomentForecast } from './LastUpdatedMoment';
import { MonthlyUsageTable } from './MonthlyUsageTable';
import { getMergedForecastRecords, useItemInheritanceData } from './sidePane/sidePane.utils';
import { UsageInheritanceTabContent } from './UsageInheritanceTabContent';

export enum InventoryUsageForecastViewTab {
  UsageForecast = 'usage-forecast',
  MonthlyUsage = 'monthly-usage',
  UsageLines = 'usage-lines',
  UsageInheritance = 'usage-inheritance',
}

export interface InventoryUsageRecord {
  date: string;
  usage: number;
  inheritedUsage: number;
  unitOfMeasure: string;
}

export const InventoryUsageForecastView = ({
  forecastRecords,
  itemUid,
  locations,
  isLoading,
  initialTab,
  onForecastOverridesUpdated,
}: {
  forecastRecords?: SearchForecastDTO[];
  itemUid: string;
  locations?: IdNameObj[];
  isLoading: boolean;
  initialTab?: InventoryUsageForecastViewTab;
  onForecastOverridesUpdated: () => void;
}) => {
  const [filteredLocationIds, setFilteredLocationIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<InventoryUsageForecastViewTab>(
    initialTab || InventoryUsageForecastViewTab.UsageForecast,
  );
  const [dateFilter, setDateFilter] = useState<DateFilter>(getDefaultDateFilter());
  const [forecastOverrideInputs, setForecastOverrideInputs] = useState<MappedUsageForecastRecord[]>([]);
  const [usageLineOverrideInputs, setUsageLineOverrideInputs] = useState<UsageLineOverride[]>([]);
  const itemInheritanceData = useItemInheritanceData(forecastRecords, itemUid);

  function onChangeUsageLine(usageLineOverride: UsageLineOverride) {
    setUsageLineOverrideInputs((prevInputs) => {
      const updatedInputs = [...prevInputs];
      const existingIndex = updatedInputs.findIndex((input) => input.usageId === usageLineOverride.usageId);

      if (existingIndex !== -1) {
        updatedInputs[existingIndex] = usageLineOverride;
      } else {
        updatedInputs.push(usageLineOverride);
      }

      return updatedInputs;
    });
  }

  const filteredHits = forecastRecords?.filter((hit) => {
    const hitLocationId = splitIdNameStr(hit.location).foreignId;
    return filteredLocationIds.length > 0 ? filteredLocationIds.includes(hitLocationId) : true;
  });

  const record = getMergedForecastRecords(filteredHits);

  const { data: forecastOverridesData } = useCoreApi(schemas.ml.getDemandForecastOverrides, {
    queryParams: {
      filter: {
        itemIds: [itemUid],
        ...(record && record?.location ? { locationIds: [splitIdNameStr(record?.location).foreignId] } : {}),
      },
    },
  });

  const { data: usageOverridesData } = useCoreApi(schemas.ml.getDemandUsageOverrides, {
    queryParams: {
      itemIds: [itemUid],
      locationIds: locations ? locations.map((loc) => loc.foreignId) : [],
    },
  });

  const handleMonthlyUsageLinesAction = (fromDate: Moment, toDate: Moment) => {
    setDateFilter({
      from: fromDate,
      to: toDate,
    });
    setActiveTab(InventoryUsageForecastViewTab.UsageLines);
  };

  function handleChangeForecastOverride(updatedRecord: MappedUsageForecastRecord) {
    const updatedForecastOverrides = [...forecastOverrideInputs];
    const existingRecordIdx = forecastOverrideInputs.findIndex((r) => r.date === updatedRecord.date);

    if (existingRecordIdx > -1) {
      updatedForecastOverrides[existingRecordIdx] = updatedRecord;
    } else {
      updatedForecastOverrides.push(updatedRecord);
    }
    setForecastOverrideInputs(updatedForecastOverrides);
  }

  function handleClearForecastOverrides() {
    const clearedOverrides = usageAndForecastRecords
      .filter((r) => r.type === RecordType.Forecast && !!r.baseQuantity && r.baseQuantity !== r.quantity)
      .map((r) => ({ ...r, quantity: r.baseQuantity! }));
    setForecastOverrideInputs(clearedOverrides);
  }

  const forecastOverrides: MappedUsageForecastRecord[] = (forecastOverridesData?.items || []).map((item) => ({
    date: item.forecastDate,
    quantity: item.overrideQty,
    type: RecordType.Forecast,
    note: item.overrideNote,
    updatedBy: item.updatedByUserName,
    updatedAt: item.updatedAt,
  }));

  const usageAndForecastRecords = getMappedUsageAndForecastRecords(
    record,
    forecastOverrides,
    usageOverridesData?.items || [],
  );

  return (
    <>
      {locations && locations.length > 1 && (
        <FilterBarBox>
          <AsyncMultiSelect
            selectProps={{
              options: locations.map((l) => ({
                value: l.foreignId,
                label: l.name,
              })),
            }}
            label="Location"
            queryPlaceholder="Search locations"
            selectedValues={filteredLocationIds}
            onSelectedValuesChange={(values) => setFilteredLocationIds(values)}
            icon={<EnvironmentOutlined />}
          />
        </FilterBarBox>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: '12px' }}>
        <LastUpdatedMomentForecast />
      </div>
      {isLoading || record ? (
        <>
          {record ? (
            <ForecastChart
              data={[
                ...record.historical_dates.map((_, i) => ({
                  date: moment(record.historical_dates[i]).valueOf(),
                  usage: record.historical_demand[i],
                  inherited: record.historical_inherited_demand[i],
                  type: ForecastDataType.Usage,
                  unitOfMeasure: record.unit_of_measure,
                })),
                ...record.forecast_dates.map((_, i) => ({
                  date: moment(record.forecast_dates[i]).valueOf(),
                  baseForecast: record.forecast_demand_base[i],
                  forecast:
                    forecastOverrideInputs.find((fo) => fo.date === record.forecast_dates[i])?.quantity ??
                    record.forecast_demand[i],
                  type: ForecastDataType.Forecast,
                  unitOfMeasure: record.unit_of_measure,
                })),
              ]}
            />
          ) : (
            <SmallLoader />
          )}
          <Tabs
            activeTab={activeTab}
            onChange={(activeKey) => setActiveTab(activeKey as InventoryUsageForecastViewTab)}
            tabs={[
              {
                header: 'Usage & Forecast',
                tabKey: InventoryUsageForecastViewTab.UsageForecast,
                content: (
                  <>
                    <div
                      className={css`
                        margin-bottom: 20px;
                      `}
                    >
                      Manually adjust forecast values for items with unpredictable or limited demand history with
                      optional notes for context. Overrides are used in planning and can be updated or removed anytime.
                      Learn more{' '}
                      <ExternalLink to="https://help.recurrency.com/hc/en-us/articles/32885707944219-Forecast-Overrides">
                        here
                      </ExternalLink>
                      .
                    </div>
                    <ForecastOverrideTable
                      isLoading={isLoading}
                      record={record}
                      usageAndForecastRecords={usageAndForecastRecords}
                      forecastOverrideInputs={forecastOverrideInputs}
                      onClearForecastOverrides={handleClearForecastOverrides}
                      onForecastChange={handleChangeForecastOverride}
                      onForecastOverridesUpdated={onForecastOverridesUpdated}
                    />
                  </>
                ),
              },
              record
                ? {
                    header: 'Monthly Usage',
                    tabKey: InventoryUsageForecastViewTab.MonthlyUsage,
                    content: (
                      <MonthlyUsageTable
                        parentIsLoading={isLoading}
                        itemId={record.item_uid}
                        locationId={splitIdNameStr(record.location).foreignId}
                        onLinesActionClick={handleMonthlyUsageLinesAction}
                        usageOverrides={usageOverridesData?.items}
                      />
                    ),
                  }
                : null,
              {
                header: 'Usage Lines',
                tabKey: InventoryUsageForecastViewTab.UsageLines,
                content: (
                  <>
                    <div
                      className={css`
                        margin-bottom: 20px;
                      `}
                    >
                      Manually adjust usage values or exclude outliers to correct data and improve forecast accuracy.
                      Overrides are saved with optional notes and can be updated or reverted anytime. Learn more{' '}
                      <ExternalLink to="https://help.recurrency.com/hc/en-us/articles/32886114205595-Usage-Data-Overrides">
                        here
                      </ExternalLink>
                      .
                    </div>
                    <DemandUsageLinesTable
                      itemUid={itemUid}
                      locationId={splitIfIdNameStr(record?.location)?.foreignId ?? ''}
                      dateFilter={dateFilter}
                      setDateFilter={setDateFilter}
                      usageLineOverrideInputs={usageLineOverrideInputs}
                      onChangeUsageLine={onChangeUsageLine}
                      onClearUsageLines={() => setUsageLineOverrideInputs([])}
                      usageOverrides={usageOverridesData?.items}
                    />
                  </>
                ),
              },
              {
                header: 'Usage Inheritance',
                tabKey: InventoryUsageForecastViewTab.UsageInheritance,
                content: (
                  <UsageInheritanceTabContent
                    itemInheritanceData={itemInheritanceData}
                    descendantItemId={itemUid}
                    descendantItemCode={record?.item_id ?? ''}
                  />
                ),
              },
            ].filter(truthy)}
          />
        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </>
  );
};

export function getUsageTableRowsFromForecastRecord(record: SearchForecastDTO | undefined) {
  return record
    ? record.historical_dates
        .map((value, index) => ({
          date: value,
          usage: record.historical_demand[index],
          inheritedUsage: record.historical_inherited_demand[index],
          unitOfMeasure: record.unit_of_measure,
        }))
        .sort((a, b) => (a.date < b.date ? 1 : -1))
    : [];
}
